/**
 * Self Improvement Loop — the Revision Engine.
 *
 * Takes a Creative Plan and the Art Director's review, and addresses the notes
 * — only the notes. It never generates design, never produces Design JSON, and
 * never touches what it must preserve.
 *
 * Pure and deterministic: the same plan and review always yield the same
 * revision.
 */

import { OFFER_LED_PURPOSES } from '../thinking';
import {
  REVISION_SCHEMA_ID, REVISION_VERSION, PRESERVED_PLAN_KEYS, IMPROVABLE_PLAN_KEYS,
  CONFIDENCE_RECOVERY,
} from './revisionSchema';
import { STRATEGY_BY_RULE } from './strategies';

const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const round2 = (n) => Math.round(n * 100) / 100;
const clamp01 = (n) => Math.max(0, Math.min(1, n));

const buildContext = (inputs) => {
  const plan = asObject(inputs.plan);
  const research = asObject(inputs.research);
  const hierarchy = plan.hierarchyStrategy && plan.hierarchyStrategy.choice;
  const purpose = asObject(research.request).purpose;
  return {
    plan,
    research,
    genome: asObject(inputs.genome),
    constraints: asObject(inputs.constraints),
    patterns: Array.isArray(inputs.patterns) ? inputs.patterns.filter((p) => p && typeof p === 'object') : [],
    // A promotion still leads with its offer even when the hierarchy has been damaged.
    offerLed: OFFER_LED_PURPOSES.includes(purpose) || (Array.isArray(hierarchy) && hierarchy[0] === 'offer'),
  };
};

/**
 * Improve a Creative Plan against a Creative Review.
 *
 * @param {Object} inputs
 * @param {Object} inputs.plan          the Creative Plan to improve.
 * @param {Object} inputs.review        the Art Director's Creative Review.
 * @param {Object} inputs.research      Research Brief (preserved; used as grounding).
 * @param {Object} inputs.genome        Style Genome (preserved; the language to return to).
 * @param {Object[]} inputs.patterns    selected Library Patterns.
 * @param {Object} [inputs.constraints] Memory constraints (preserved).
 * @returns {Object} { schema, version, plan, patterns, revisionSummary, changedDecisions, reasoning, confidenceDelta, unaddressed }
 */
export const reviseCreativePlan = (inputs = {}) => {
  const ctx = buildContext(asObject(inputs));
  const review = asObject(inputs.review);
  const weaknesses = Array.isArray(review.weaknesses) ? review.weaknesses : [];

  const changedDecisions = [];
  const reasoning = [];
  const unaddressed = [];
  const claimed = new Set();
  let patterns = ctx.patterns;
  let recovery = 0;

  // Weaknesses arrive most urgent first; the first strategy to claim a decision owns it.
  weaknesses.forEach((weakness) => {
    const strategy = STRATEGY_BY_RULE[weakness.rule];
    if (!strategy) {
      // No legitimate revision exists: this needs a different language, which is the Thinking Engine's call.
      unaddressed.push({ rule: weakness.rule, detects: weakness.detects, reason: 'Requires re-thinking, not revision — the Revision Engine may not change the design language, intent, or memory constraints.' });
      return;
    }

    const result = asObject(strategy.apply({ ...ctx, patterns }));
    let touched = false;

    (Array.isArray(result.decisions) ? result.decisions : []).forEach(({ key, choice, why, basis }) => {
      if (!IMPROVABLE_PLAN_KEYS.includes(key) || claimed.has(key)) return;
      claimed.add(key);
      touched = true;
      changedDecisions.push({
        key,
        addressing: weakness.rule,
        from: ctx.plan[key] ? ctx.plan[key].choice : undefined,
        to: choice,
        decision: { choice, why, basis },
      });
      reasoning.push(why);
    });

    if (Array.isArray(result.patterns) && result.patterns.length !== patterns.length) {
      patterns = result.patterns;
      touched = true;
      changedDecisions.push({
        key: 'patterns',
        addressing: weakness.rule,
        from: ctx.patterns.map((p) => p.id),
        to: patterns.map((p) => p.id),
        decision: { choice: patterns.map((p) => p.id), why: result.note, basis: [`review:${weakness.rule}`] },
      });
      if (result.note) reasoning.push(result.note);
    }

    if (touched) recovery += CONFIDENCE_RECOVERY[weakness.severity] || 0;
    else unaddressed.push({ rule: weakness.rule, detects: weakness.detects, reason: 'Already addressed by an earlier revision this round.' });
  });

  // Apply the improved decisions. Preserved keys are never in `changedDecisions`,
  // so the plan's intent, language, and memory constraints survive untouched.
  const improved = { ...ctx.plan };
  changedDecisions
    .filter((c) => c.key !== 'patterns')
    .forEach((c) => { improved[c.key] = c.decision; });

  const before = typeof ctx.plan.confidence === 'number' ? ctx.plan.confidence : 0.5;
  const after = changedDecisions.length ? round2(clamp01(before + recovery)) : before;
  improved.confidence = after;

  const summary = changedDecisions.length === 0
    ? (weaknesses.length === 0 ? 'No revision requested — the review raised nothing to address.' : 'No meaningful revision remains: the outstanding notes require re-thinking, not revision.')
    : `Addressed ${changedDecisions.length} of ${weaknesses.length} note${weaknesses.length === 1 ? '' : 's'} by improving ${changedDecisions.map((c) => c.key).join(', ')}.`;

  return {
    schema: REVISION_SCHEMA_ID,
    version: REVISION_VERSION,
    plan: improved,
    patterns,
    revisionSummary: summary,
    changedDecisions,
    reasoning,
    confidenceDelta: round2(after - before),
    unaddressed,
    preserved: PRESERVED_PLAN_KEYS,
  };
};
